import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ContextInputPanel, { type ContextResult } from "./ContextInputPanel";
import CaptureHeader from "./CaptureHeader";

export interface PhotoCaptureData {
  imageFile: File;
  imagePreviewUrl: string;
  imageFiles: File[];
  imagePreviewUrls: string[];
  memoryType: "photo" | "drawing";
  contextBlob?: Blob;
  contextText?: string;
  approvalStatus?: 'pending' | 'private';
}

interface PhotoCaptureProps {
  memoryType: "photo" | "drawing";
  maxPhotos?: number;
  onSave: (data: PhotoCaptureData) => void;
  onFinishLater?: (data: PhotoCaptureData) => void;
  onCancel: () => void;
  accentColor?: string;
  autoStart?: boolean;
}

type Step = "pick" | "preview";

const MAX_BYTES = 10 * 1024 * 1024;

const PhotoCapture = ({ memoryType, maxPhotos = 1, onSave, onFinishLater, onCancel, accentColor, autoStart }: PhotoCaptureProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("pick");
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const addMoreRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => { previewUrls.forEach(u => URL.revokeObjectURL(u)); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoStart) return;
    const t = setTimeout(() => cameraRef.current?.click(), 150);
    return () => clearTimeout(t);
  }, [autoStart]);

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const arr = Array.from(incoming);
    const valid: File[] = [];
    for (const f of arr) {
      if (f.size > MAX_BYTES) { toast.error(t("toast.photoSizeExceeded")); continue; }
      valid.push(f);
    }
    if (!valid.length) return;
    const remaining = maxPhotos - imageFiles.length;
    const toAdd = valid.slice(0, remaining);
    if (!toAdd.length) return;
    const newUrls = toAdd.map(f => URL.createObjectURL(f));
    setImageFiles(prev => [...prev, ...toAdd]);
    setPreviewUrls(prev => [...prev, ...newUrls]);
    setStep("preview");
  };

  const handleRemove = (index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    const newFiles = imageFiles.filter((_, i) => i !== index);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    setPreviewUrls(newUrls);
    if (newFiles.length === 0) setStep("pick");
  };

  const handleBack = () => {
    previewUrls.forEach(u => URL.revokeObjectURL(u));
    setImageFiles([]);
    setPreviewUrls([]);
    setStep("pick");
  };

  const handleProcess = (ctx: ContextResult) => {
    if (!imageFiles.length || !previewUrls.length) return;
    onSave({
      imageFile: imageFiles[0],
      imagePreviewUrl: previewUrls[0],
      imageFiles,
      imagePreviewUrls: previewUrls,
      memoryType,
      contextBlob: ctx.contextBlob,
      contextText: ctx.contextText,
      approvalStatus: ctx.approvalStatus,
    });
  };

  const atCap = imageFiles.length >= maxPhotos;
  const isMultiPhoto = maxPhotos > 1;
  const typeLabel = memoryType === "photo" ? "photo" : "drawing";
  const titleKey = memoryType === "photo" ? "capture.title.photo" : "capture.title.drawing";

  return (
    <div className="flex flex-col gap-4 w-full px-5 pt-8 pb-4">
      <CaptureHeader
        title={t(titleKey)}
        onBack={step === "preview" ? handleBack : undefined}
        backLabel={t("capture.preview.retake")}
      />

      {/* Hidden file inputs */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={galleryRef} type="file" accept="image/*" multiple={isMultiPhoto} className="sr-only"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      <input ref={addMoreRef} type="file" accept="image/*" multiple className="sr-only"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

      {/* ── pick ── */}
      {step === "pick" && (
        <>
          <div className="-mb-1">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">How would you like to add it?</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{t("capture.photo.pickHint")}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full">
            <button type="button" onClick={() => cameraRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-border/60 bg-card py-5 px-3 transition-all active:scale-95 hover:border-primary/40 hover:shadow-sm">
              <span className="text-3xl leading-none">📷</span>
              <span className="text-[13px] font-medium text-foreground">Take photo</span>
            </button>
            <button type="button" onClick={() => galleryRef.current?.click()}
              className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-border/60 bg-card py-5 px-3 transition-all active:scale-95 hover:border-primary/40 hover:shadow-sm">
              <span className="text-3xl leading-none">🖼️</span>
              <span className="text-[13px] font-medium text-foreground">{t("capture.photo.fromGallery")}</span>
            </button>
          </div>
          <button type="button" onClick={onCancel}
            className="text-[13px] text-muted-foreground text-center hover:text-foreground transition-colors py-1">
            Cancel
          </button>
        </>
      )}

      {/* ── preview + context ── */}
      {step === "preview" && (
        <>
          {/* Multi-photo: thumbnail strip */}
          {isMultiPhoto ? (
            <>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {previewUrls.map((url, i) => (
                  <div key={url} className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleRemove(i)}
                      className="relative block rounded-xl overflow-hidden border-2 border-border focus-visible:ring-2 active:scale-95 transition-transform"
                      style={{ width: 72, height: 72 }}
                      aria-label={t("aria.removePhoto", { index: i + 1 })}
                    >
                      <img src={url} alt={`${typeLabel} ${i + 1}`} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 active:opacity-100 transition-opacity">
                        <span className="text-white text-xs font-semibold">✕</span>
                      </div>
                    </button>
                    {i === 0 && (
                      <span className="absolute bottom-1 left-0 right-0 text-center text-[9px] font-semibold bg-black/55 text-white rounded-b-xl px-1 py-0.5 leading-none pointer-events-none">
                        {t("capture.photo.cover")}
                      </span>
                    )}
                  </div>
                ))}

                {/* Add-more slot (hidden at cap) */}
                {!atCap && (
                  <button
                    type="button"
                    onClick={() => addMoreRef.current?.click()}
                    className="flex-shrink-0 flex flex-col items-center justify-center gap-0.5 rounded-xl border-2 border-dashed border-border bg-muted text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                    style={{ width: 72, height: 72 }}
                    aria-label={t("capture.photo.addMore")}
                  >
                    <span className="text-xl leading-none">+</span>
                    <span className="text-[9px]">{t("capture.photo.addMore")}</span>
                  </button>
                )}
              </div>

              {/* Counter + upsell for non-Seedling at cap */}
              <div className="flex items-center justify-between -mt-1">
                <p className="text-[12px] text-muted-foreground">
                  {t("capture.photo.counter", { count: imageFiles.length, max: maxPhotos })}
                </p>
              </div>
            </>
          ) : (
            /* Single-photo (Seedling): original preview layout */
            <>
              {previewUrls[0] && (
                <img src={previewUrls[0]} alt={typeLabel} className="w-full rounded-2xl object-cover" style={{ maxHeight: 200 }} />
              )}
              {/* Upsell hint for Seedling at cap */}
              {atCap && (
                <button
                  type="button"
                  onClick={() => navigate("/upgrade")}
                  className="text-[12px] text-primary font-medium text-center hover:underline -mt-1"
                >
                  {t("capture.photo.upgradeHint")}
                </button>
              )}
            </>
          )}

          <ContextInputPanel
            onProcess={handleProcess}
            onSaveLater={onFinishLater ? () => {
              if (!imageFiles.length || !previewUrls.length) return;
              onFinishLater({
                imageFile: imageFiles[0],
                imagePreviewUrl: previewUrls[0],
                imageFiles,
                imagePreviewUrls: previewUrls,
                memoryType,
              });
            } : undefined}
            accentColor={accentColor}
          />
        </>
      )}
    </div>
  );
};

export default PhotoCapture;
